import { defineComponent as _defineComponent } from 'vue'
import {ref, reactive, computed, defineAsyncComponent, onMounted} from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import VBreadcrumbs from "@/components/vBreadcrumbs.vue";
import UserDetails from "@/models/User/UserDetails";
import {useStore} from "@/hooks/useStore";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import {useRouter} from "vue-router/composables";
import VModal from "@/components/vModal.vue";
import VTable from "@/components/vTable.vue";
import invoiceRepository from "@/utils/API/InvoiceRepository";
import {Filter, Ordering, OrderType} from "@/models/ListRequest";
import {Invoice} from "@/models/Invoice/Invoice";
import VSelectFx from "@/components/vSelectFx.vue";
import {round} from "@/utils/Helpers";


export default /*#__PURE__*/_defineComponent({
  __name: 'RecipeInvoice',
  setup(__props) {

let translations = ref(useTranslations());
const vSelectFx = defineAsyncComponent(() => import("@/components/vSelectFx.vue"));
const store = useStore();
const router = useRouter();
const page = ref(1);
const recipeInvoices = ref<any[]>([]);
const totalCount = ref(0);
const searchTerm = ref("");
const count = ref(0);
const filter = ref<Filter[]>([
  { name: "", value: "" },
]);

const ordering = ref<Ordering>({
  field: "",
  orderType: OrderType.Asc,
});
const loading = ref(false);
const deletingModal = reactive({
  isOpen: false,
  ids: [] as number[],
});

const tableHeaders = ref([
  {
    name: "Invoice Name",
    key: "ItemName",
    sortable: true,
  },
  {
    name: "Total Cost",
    key: "Cost",
  },
  {
    name: "Created Date",
    key: "CreatedOn",
    searchable: false,
    searchString: "",
    sortable: true,
  },
]);

const userDetails: UserDetails = null;
const hasRecipeCosts = computed(() => recipeInvoices.value.length > 0);

const filteredInvoice = computed(() =>
    recipeInvoices.value.filter((x) =>
        x.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
);

const recipeInvoiceForGrid = computed(() =>
  filteredInvoice.value.map((x) => {
    return {
      meta: {
        id: x.id,
        link: x.name,
      },
      e1: calculateTotalCost(x),
      e2: x.dateCreated,
    };
  })
);

function calculateTotalCost(recipeInvoice : Invoice){
  let sum = 0;
  (recipeInvoice.recipeCosts || []).forEach((c) => { sum += c.costAtTimeOfInvoice || 0; });
  (recipeInvoice.ingredientCosts || []).forEach((c) => { sum += c.costAtTimeOfInvoice || 0; });
  (recipeInvoice.additionalCosts || []).forEach((c) => { sum += c.costAtTimeOfInvoice || 0; });

  return round(sum, 2);
}

function exportFile() {
  return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Invoice/ExportInvoicesSummary`;
}

function translate(key: string) {
  return translations.value.get(key);
}

function changePage(args: any) {
  const pageNum = args[0];
  const filtersFromTable = args[2];
  const orderingsFromTable = args[3];

  page.value = pageNum;
  ordering.value =
    orderingsFromTable && orderingsFromTable.length > 0
      ? orderingsFromTable[0]
      : { field: "", orderType: OrderType.Asc };

  filter.value =
    filtersFromTable && filtersFromTable.length > 0
      ? filtersFromTable
      : [];

  requestRecipesInvoices();
}
function order(filters: Filter[], orders: Ordering) {
  filter.value = filters;
  ordering.value = orders;
  requestRecipesInvoices();
}

function goOnEditRecipeCostPage(recipeInvoiceId: string){
  router.push({
    name: "recipeInvoiceEdit",
    params: { id: recipeInvoiceId },
  });
}

function deleteRecipeCost(ids: number[] | number){
  deletingModal.isOpen = true;
  deletingModal.ids = Array.isArray(ids) ? ids : [ids];
}

async function submitDelete() {
  try {
    await invoiceRepository.deleteInvoices(deletingModal.ids);
    recipeInvoices.value = recipeInvoices.value.filter(
      (x) => !deletingModal.ids.includes(x.id)
    );
  }
  finally {
    deletingModal.isOpen = false;
    deletingModal.ids = [];
  }
}

function addNewRecipeInvoice(){
  router.push({
    name: "recipeInvoiceAdd",
  });
}

async function requestRecipesInvoices() {
  loading.value = true;
  try {
    const response = await invoiceRepository.getInvoices();

    recipeInvoices.value = response.data;
    count.value = response.data.length;
    totalCount.value = response.data.length;
  }
  finally {
    loading.value = false;
  }
}

onMounted(async() => {
  try {
    await store.value.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "recipe costing", "ingredient cost"],
    });
    document.title =
      translate("rec_invoice") + " " + translate("meta_title_postfix");
  }
  catch (error: any) {
  }
  await requestRecipesInvoices();
});

return { __sfc: true,translations, vSelectFx, store, router, page, recipeInvoices, totalCount, searchTerm, count, filter, ordering, loading, deletingModal, tableHeaders, userDetails, hasRecipeCosts, filteredInvoice, recipeInvoiceForGrid, calculateTotalCost, exportFile, translate, changePage, order, goOnEditRecipeCostPage, deleteRecipeCost, submitDelete, addNewRecipeInvoice, requestRecipesInvoices, VBreadcrumbs, VModal, VTable, VSelectFx }
}

})